import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TablePagination,
  TableSortLabel,
  TextField,
  Typography,
  Button,
  InputAdornment,
  Paper,
} from "@mui/material";
import { HiSearch, HiPlus } from "react-icons/hi";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import {
  useAdminOrganizationsPaginated,
  useAdminDeleteOrganization,
} from "@/api/hooks/admin.hooks";
import type { OrganizationDto } from "@/api/types/dtos";
import type { PaginatedResponse } from "@/api/types/common";
import CreateOrganizationModal from "./CreateOrganizationModal";
import EditOrganizationModal from "./EditOrganizationModal";
import DeleteOrganizationDialog from "./DeleteOrganizationDialog";
import OrganizationTableBody from "./OrganizationTableBody";

const PAGE_SIZE_OPTIONS = [10, 25, 50];
const DEBOUNCE_MS = 300;

type SortField = "name" | "idNumber" | "isActive";
type SortDirection = "asc" | "desc";

const OrganizationsTable: React.FC = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortDir, setSortDir] = useState<SortDirection>("asc");

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editOrg, setEditOrg] = useState<OrganizationDto | null>(null);
  const [deleteOrg, setDeleteOrg] = useState<OrganizationDto | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setPage(0);
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data, isLoading, isError, refetch } = useAdminOrganizationsPaginated({
    page,
    size: rowsPerPage,
    search: debouncedSearch || undefined,
    sortBy: sortField,
    sortDirection: sortDir.toUpperCase() as 'ASC' | 'DESC',
  });

  const deleteMutation = useAdminDeleteOrganization();

  const paginatedData = data?.data as PaginatedResponse<OrganizationDto> | undefined;
  const organizations: OrganizationDto[] = paginatedData?.content ?? [];
  const totalElements: number = paginatedData?.totalElements ?? 0;

  const handleChangePage = useCallback((_event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleSort = useCallback(
    (field: SortField) => {
      const isAsc = sortField === field && sortDir === "asc";
      setSortDir(isAsc ? "desc" : "asc");
      setSortField(field);
      setPage(0);
    },
    [sortField, sortDir]
  );

  const handleOrgClick = useCallback(
    (org: OrganizationDto) => {
      navigate(`/admin-v2/organizations/${org.id}`);
    },
    [navigate]
  );

  const handleDeleteConfirm = useCallback(() => {
    if (!deleteOrg) return;
    deleteMutation.mutate(deleteOrg.id, {
      onSuccess: () => {
        enqueueSnackbar("Organización eliminada exitosamente", { variant: "success" });
        setDeleteOrg(null);
        refetch();
      },
      onError: () => {
        enqueueSnackbar("Error al eliminar la organización", { variant: "error" });
      },
    });
  }, [deleteOrg, deleteMutation, enqueueSnackbar, refetch]);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <TextField
          placeholder="Buscar organizaciones..."
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ minWidth: 280, flex: "1 1 280px" }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <HiSearch size={18} style={{ opacity: 0.5 }} />
                </InputAdornment>
              ),
            },
          }}
        />
        <Button
          variant="contained"
          startIcon={<HiPlus size={16} />}
          onClick={() => setCreateModalOpen(true)}
          sx={{ flexShrink: 0 }}
        >
          Crear Organización
        </Button>
      </Box>

      {isError ? (
        <Paper sx={{ p: 3, textAlign: "center" }}>
          <Typography color="error" gutterBottom>
            Error al cargar las organizaciones
          </Typography>
          <Button variant="outlined" onClick={() => refetch()}>
            Reintentar
          </Button>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === "name"}
                      direction={sortField === "name" ? sortDir : "asc"}
                      onClick={() => handleSort("name")}
                    >
                      Nombre
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Documento</TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={sortField === "isActive"}
                      direction={sortField === "isActive" ? sortDir : "asc"}
                      onClick={() => handleSort("isActive")}
                    >
                      Estado
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <OrganizationTableBody
                isLoading={isLoading}
                organizations={organizations}
                debouncedSearch={debouncedSearch}
                skeletonRows={rowsPerPage}
                onOrgClick={handleOrgClick}
                onEdit={setEditOrg}
                onDelete={setDeleteOrg}
                onCreateClick={() => setCreateModalOpen(true)}
              />
            </Table>
          </TableContainer>

          <TablePagination
            component="div"
            count={totalElements}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            labelRowsPerPage="Filas por página"
            labelDisplayedRows={({ from, to, count }) =>
              `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`
            }
          />
        </>
      )}

      <CreateOrganizationModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={() => {
          setCreateModalOpen(false);
          refetch();
        }}
      />

      <EditOrganizationModal
        open={!!editOrg}
        organization={editOrg}
        onClose={() => setEditOrg(null)}
        onSuccess={() => {
          setEditOrg(null);
          refetch();
        }}
      />

      <DeleteOrganizationDialog
        open={!!deleteOrg}
        organization={deleteOrg}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteOrg(null)}
        loading={deleteMutation.isPending}
      />
    </Box>
  );
};

export default OrganizationsTable;
